/**
 * AgreementsSection — list of all agreements for a customer (profile tab equivalent).
 * Click an item → opens the AgreementDialog for that loan (view PDF, re-share).
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { FileText, Download, MessageCircle, ShieldAlert } from "lucide-react";
import AgreementDialog from "./AgreementDialog";
import { buildPublicAgreementUrl } from "../lib/publicUrl";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_PILL = {
  draft: { cls: "bg-amber-100 text-amber-700 border-amber-300", label: "Pending Signing" },
  signed: { cls: "bg-emerald-100 text-emerald-700 border-emerald-300", label: "Signed" },
};

export default function AgreementsSection({ customerId, customer }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeLoanId, setActiveLoanId] = useState(null);

  const load = useCallback(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}/agreements`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const openOne = (loanId) => {
    setActiveLoanId(loanId);
    setOpen(true);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="agreements-section">
      <div className="p-5 border-b border-slate-200 flex items-center gap-2">
        <FileText className="w-4 h-4 text-emerald-600" />
        <div className="font-display font-semibold">Agreements</div>
        <span className="text-xs text-slate-500">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-5 text-sm text-slate-500">
          Koi agreement nahi mila. Nayi loan banayein to agreement automatically draft ban jata hai.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((a) => {
            const pill = STATUS_PILL[a.status] || STATUS_PILL.draft;
            const pdfUrl = `${API_BASE}/api/agreements/${a.id}/pdf?shop=${a.shop_id}`;
            // Customer-facing URL uses HisaabPro domain (never expose backend hostname in WA shares)
            const publicPdfUrl = buildPublicAgreementUrl(a.id, a.shop_id);
            const waMessage = `Assalam o Alaikum ${a.snapshot?.customer_name || ""},\n\nAap ka installment agreement (#${a.agreement_number}) tayyar hai. Download kijiye:\n${publicPdfUrl}`;
            const waLink = customer?.mobile
              ? `https://wa.me/${(customer.mobile || "").replace(/\D/g, "").replace(/^0/, "92")}?text=${encodeURIComponent(waMessage)}`
              : null;
            return (
              <div key={a.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`agreement-row-${a.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <button
                    type="button"
                    onClick={() => openOne(a.loan_id)}
                    className="font-display font-semibold text-emerald-700 hover:underline"
                  >
                    {a.agreement_number}
                  </button>
                  <div className="text-xs text-slate-500">
                    {a.snapshot?.product_name || "—"} · {a.snapshot?.agreement_date || a.created_at?.slice(0,10)}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${pill.cls}`}>
                  {pill.label}
                </span>
                {a.activation_override && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-300 inline-flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" /> Override
                  </span>
                )}
                <a href={pdfUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-2.5 py-1 rounded-md text-xs font-semibold"
                   data-testid={`agreement-pdf-${a.id}`}>
                  <Download className="w-3 h-3" /> PDF
                </a>
                {waLink && a.status === "signed" && (
                  <a href={waLink} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-md text-xs font-semibold"
                     data-testid={`agreement-wa-${a.id}`}>
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
      <AgreementDialog open={open} onOpenChange={setOpen} loanId={activeLoanId} onLoanActivated={load} />
    </div>
  );
}
